import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  AppState,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { storage } from '../../utils/storage';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
  sessionId?: string;
}

interface ImportStats {
  totalRecords: number;
  uuidMatches: number;
  nameMatches: number;
  notFound: number;
  duplicates: number;
  totalAmount: number;
  accuracy: number;
  dateRange?: string;
  batchId?: string;
  sourceFile?: string;
  importType?: string;
  fileHash?: string;
  processedRecords?: number;
  rawRecordsSaved?: number;
}

export const UberImportScreenFixed = () => {
  const navigation = useNavigation();
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import Uber data',
  });
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const appState = useRef(AppState.currentState);

  // Generate unique session ID for this import session
  useEffect(() => {
    const newSessionId = `uber_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // Load any previous session state
    loadSessionState(newSessionId);
  }, []);

  // Handle app state changes (background/foreground)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription?.remove();
  }, []);

  const handleAppStateChange = (nextAppState: string) => {
    if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
      // App came to foreground, restore state if processing
      if (isProcessing) {
        restoreProcessingState();
      }
    } else if (nextAppState.match(/inactive|background/) && isProcessing) {
      // App going to background, save state
      saveSessionState();
    }
    appState.current = nextAppState;
  };

  const saveSessionState = async () => {
    try {
      const state = {
        selectedFile,
        progress,
        importStats,
        isProcessing,
        sessionId,
        timestamp: Date.now()
      };
      await storage.setItem(`uber_import_${sessionId}`, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save session state:', error);
    }
  };

  const loadSessionState = async (sid: string) => {
    try {
      const savedState = await storage.getItem(`uber_import_${sid}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        // Only restore if less than 30 minutes old
        if (Date.now() - state.timestamp < 30 * 60 * 1000) {
          setSelectedFile(state.selectedFile);
          setProgress(state.progress);
          setImportStats(state.importStats);
          setIsProcessing(state.isProcessing);
        } else {
          // Clear old state
          await storage.removeItem(`uber_import_${sid}`);
        }
      }
    } catch (error) {
      console.warn('Failed to load session state:', error);
    }
  };

  const restoreProcessingState = async () => {
    try {
      // Check if there's an ongoing import batch in the database
      if (progress.sessionId) {
        const { data: batchStatus } = await supabase
          .from('uber_income_raw')
          .select('batch_id, imported_at')
          .eq('session_id', progress.sessionId)
          .order('imported_at', { ascending: false })
          .limit(1);

        if (batchStatus && batchStatus.length > 0) {
          // Import was completed while in background
          setProgress({
            ...progress,
            phase: 'complete',
            message: 'Import completed while app was in background'
          });
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.warn('Failed to restore processing state:', error);
    }
  };

  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload CSV file from Uber' },
    { id: 2, name: 'File Validation', description: 'Validate file format and structure' },
    { id: 3, name: 'Duplicate Detection', description: 'Check for duplicate transactions' },
    { id: 4, name: 'UUID Matching', description: 'Match drivers using Uber UUIDs' },
    { id: 5, name: 'Data Processing', description: 'Process earnings with chunked batches' },
    { id: 6, name: 'Database Update', description: 'Save data to accounting system' },
  ];

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        setSelectedFile(result.assets[0]);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: `Selected: ${result.assets[0].name}`,
          sessionId
        });
        // Clear any previous import stats
        setImportStats(null);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick file');
      console.error('File picker error:', error);
    }
  };

  const performOptimizedImport = async (skipDuplicateCheck = false) => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a file first');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Step 1: File Upload & Reading
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Reading file...', 
        sessionId,
        details: 'Loading CSV content into memory'
      });
      
      let content: string;
      if (selectedFile.uri) {
        const response = await fetch(selectedFile.uri);
        content = await response.text();
      } else {
        content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(selectedFile as any);
        });
      }
      
      // Step 2: File Validation
      setProgress({ 
        phase: 'processing', 
        currentStep: 2, 
        totalSteps: 6, 
        message: 'Validating file format...',
        sessionId,
        details: 'Checking CSV structure and headers'
      });

      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File appears to be empty or invalid');
      }

      const headers = lines[0].split(',');
      console.log('CSV Headers:', headers);
      
      // Enhanced date range detection
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      console.log('📅 Detected date range:', dateRange);
      console.log('📁 Filename:', selectedFile?.name);
      
      // Try to extract actual dates from CSV content
      const dataLines = lines.slice(1, Math.min(lines.length, 10)); // Check first 10 data lines
      const csvDates = [];
      dataLines.forEach(line => {
        const cells = line.split(',');
        // Look for date columns (adjust indices based on your CSV structure)
        for (let i = 0; i < Math.min(cells.length, 5); i++) {
          const cell = cells[i];
          if (cell && /\d{4}-\d{2}-\d{2}/.test(cell)) {
            csvDates.push(cell);
          }
        }
      });
      
      if (csvDates.length > 0) {
        csvDates.sort();
        const actualRange = {
          startDate: csvDates[0],
          endDate: csvDates[csvDates.length - 1]
        };
        console.log('📊 Actual CSV date range:', actualRange);
        // Update dateRange with actual dates if found
        Object.assign(dateRange, actualRange);
        console.log('📅 Final date range to use:', dateRange);
      }

      // Step 3: Duplicate Detection (if not skipped)
      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 3, 
          totalSteps: 6, 
          message: 'Checking for duplicates...',
          sessionId,
          details: 'Verifying against existing data'
        });

        const duplicateResult = await checkForDuplicatesOptimized(lines);
        if (duplicateResult.hasDuplicates) {
          setIsProcessing(false);
          
          if (duplicateResult.isDuplicateFile) {
            Alert.alert(
              'Duplicate File Detected',
              `This file was already imported. Records: ${duplicateResult.duplicateCount}`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Force Re-import', onPress: () => performOptimizedImport(true) }
              ]
            );
            return;
          } else {
            Alert.alert(
              'Potential Duplicates',
              `Found ${duplicateResult.duplicateCount} potential duplicates.`,
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'Continue', onPress: () => processDataChunked(lines) }
              ]
            );
            return;
          }
        }
      }

      await processDataChunked(lines);

    } catch (error) {
      console.error('Import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        sessionId,
        details: error.message || 'An unexpected error occurred'
      });
      setIsProcessing(false);
    }
  };

  const checkForDuplicatesOptimized = async (lines: string[]) => {
    try {
      const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
      const fileHash = await calculateFileHash(lines.join('\n'));
      
      // Quick hash-based duplicate check
      const { data: hashCheck } = await supabase
        .from('uber_income_raw')
        .select('batch_id, imported_at')
        .eq('file_hash', fileHash)
        .limit(1);

      if (hashCheck && hashCheck.length > 0) {
        return {
          hasDuplicates: true,
          isDuplicateFile: true,
          duplicateCount: lines.length - 1
        };
      }

      // Basic date range check (faster than complex queries)
      const { data: dateCheck } = await supabase
        .from('accounting_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('category', 'uber')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      return {
        hasDuplicates: (dateCheck || 0) > 0,
        isDuplicateFile: false,
        duplicateCount: dateCheck || 0
      };
    } catch (error) {
      console.warn('Duplicate check failed:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  const processDataChunked = async (lines: string[]) => {
    // Step 4: UUID Matching
    setProgress({ 
      phase: 'processing', 
      currentStep: 4, 
      totalSteps: 6, 
      message: 'Matching drivers...',
      sessionId,
      details: 'Finding drivers by UUID in database'
    });

    const batchId = crypto.randomUUID();
    const fileHash = await calculateFileHash(lines.join('\n'));
    const dateRange = extractDateRangeFromFilename(selectedFile?.name || '');
    
    let processedCount = 0;
    let totalAmount = 0;
    let uuidMatches = 0;
    let nameMatches = 0;
    let notFoundCount = 0;
    let companyTotalAmount = 0;
    let totalDriverSum = 0;

    const chunkSize = 50; // Process 50 records at a time
    const dataLines = lines.slice(1); // Skip header
    const totalRecords = dataLines.length;

    // Step 5: Chunked Data Processing
    setProgress({ 
      phase: 'processing', 
      currentStep: 5, 
      totalSteps: 6, 
      message: 'Processing data in chunks...',
      sessionId,
      details: `Processing ${totalRecords} records in batches of ${chunkSize}`,
      processed: 0,
      total: totalRecords
    });

    for (let i = 0; i < dataLines.length; i += chunkSize) {
      const chunk = dataLines.slice(i, i + chunkSize);
      const chunkResults = await processChunk(chunk, batchId, fileHash, dateRange, i);
      
      processedCount += chunkResults.processed;
      totalAmount += chunkResults.totalAmount;
      uuidMatches += chunkResults.uuidMatches;
      nameMatches += chunkResults.nameMatches;
      notFoundCount += chunkResults.notFound;
      companyTotalAmount += chunkResults.companyTotal || 0;
      totalDriverSum += chunkResults.driverSum || 0;

      // Update progress
      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 6, 
        message: 'Processing data in chunks...',
        sessionId,
        details: `Processed ${Math.min(i + chunkSize, totalRecords)} of ${totalRecords} records`,
        processed: Math.min(i + chunkSize, totalRecords),
        total: totalRecords
      });

      // Save state after each chunk
      await saveSessionState();

      // Small delay to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Step 6: Create Company Transaction & Reconciliation
    setProgress({ 
      phase: 'processing', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Creating company transaction & reconciliation...',
      sessionId,
      details: 'Finalizing import'
    });

    console.log(`📊 IMPORT SUMMARY:`);
    console.log(`  Total Drivers: ${totalRecords}`);
    console.log(`  Driver Sum: ${totalDriverSum} AED`);
    console.log(`  Company Total: ${companyTotalAmount} AED`);
    console.log(`  Difference: ${Math.abs(totalDriverSum - companyTotalAmount)} AED`);

    // Create company cash transaction (bank deposit)
    let companyTransactionId = null;
    if (companyTotalAmount > 0) {
      console.log(`💰 Creating company bank deposit transaction...`);
      const { data: companyTx, error: companyTxError } = await supabase
        .from('company_cash_transactions')
        .insert({
          transaction_date: dateRange.endDate,
          transaction_type: 'bank_deposit',
          category: 'uber_deposit',
          amount: companyTotalAmount,
          description: `Uber Bank Deposit (${dateRange.startDate} to ${dateRange.endDate})`,
          reference_number: `UBER-${batchId.substring(0, 8)}`,
          related_batch_id: batchId,
          verified: true,
          notes: `${processedCount} drivers processed, total driver credit: ${totalDriverSum} AED`
        })
        .select()
        .single();

      if (companyTxError) {
        console.error('❌ Failed to create company transaction:', companyTxError);
      } else {
        companyTransactionId = companyTx?.id;
        console.log('✅ Company transaction created:', companyTransactionId);
      }
    }

    // Create batch reconciliation
    const reconciliationStatus = Math.abs(totalDriverSum - companyTotalAmount) < 0.01 ? 'matched' : 'discrepancy';
    
    console.log(`🔍 Creating batch reconciliation (Status: ${reconciliationStatus})...`);
    const { data: reconciliation, error: reconError } = await supabase
      .from('batch_reconciliation')
      .insert({
        import_batch_id: batchId,
        import_type: 'uber',
        import_date: new Date().toISOString().split('T')[0],
        period_start: dateRange.startDate,
        period_end: dateRange.endDate,
        file_company_total: companyTotalAmount,
        file_driver_sum: totalDriverSum,
        file_record_count: totalRecords,
        system_driver_sum: totalAmount,
        system_record_count: processedCount,
        company_transaction_id: companyTransactionId,
        company_transaction_amount: companyTotalAmount,
        status: reconciliationStatus,
        difference_amount: Math.abs(totalDriverSum - companyTotalAmount),
        difference_count: totalRecords - processedCount
      })
      .select()
      .single();

    if (reconError) {
      console.error('❌ Failed to create reconciliation:', reconError);
    } else {
      console.log('✅ Reconciliation created:', reconciliation?.id);
    }

    const stats: ImportStats = {
      totalRecords,
      uuidMatches: uuidMatches,
      nameMatches: nameMatches,
      notFound: notFoundCount,
      duplicates: 0,
      totalAmount,
      accuracy: Math.round(((uuidMatches + nameMatches) / totalRecords) * 100),
      dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
      batchId: batchId.substring(0, 8),
      sourceFile: selectedFile?.name || 'unknown',
      importType: 'Chunked Processing',
      fileHash: fileHash.substring(0, 16),
      processedRecords: processedCount,
      rawRecordsSaved: totalRecords
    };

    setImportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 6, 
      totalSteps: 6, 
      message: 'Import completed successfully!',
      sessionId,
      details: `${uuidMatches + nameMatches} transactions imported. Company deposit: ${companyTotalAmount} AED. Status: ${reconciliationStatus === 'matched' ? '✅ MATCHED' : '⚠️ DISCREPANCY'}`
    });
    
    setIsProcessing(false);
    
    // Clear session state
    await storage.removeItem(`uber_import_${sessionId}`);
  };

  const processChunk = async (chunk: string[], batchId: string, fileHash: string, dateRange: any, startIndex: number) => {
    const rawRecords: any[] = [];
    const transactions: any[] = [];
    let processed = 0;
    let totalAmount = 0;
    let uuidMatches = 0;
    let nameMatches = 0;
    let notFound = 0;
    let skippedRecords = 0;
    let companyTotal = 0;
    let driverSum = 0;

    console.log(`🔄 Processing chunk of ${chunk.length} records...`);

    for (let i = 0; i < chunk.length; i++) {
      const line = chunk[i].trim();
      if (!line) continue;

      const cells = line.split(',');
      const driverUuid = cells[0] || '';
      const firstName = cells[1] || '';
      const lastName = cells[2] || '';
      const driverName = `${firstName} ${lastName}`.trim();
      
      // Parse ALL Uber columns correctly
      const totalIncome = parseFloat(cells[3] || '0');           // Column 3: إجمالي الدخل (Total Income)
      const netFare = parseFloat(cells[4] || '0');               // Column 4: صافي الأجرة (Net Fare)
      const refundsAndExpenses = parseFloat(cells[5] || '0');    // Column 5: المبالغ المستردة والنفقات (Refunds & Expenses) - TOLL REFUNDS!
      const incomeAmounts = parseFloat(cells[6] || '0');         // Column 6: مبالغ الدخل (Income Amounts)
      const bankTransfer = parseFloat(cells[7] || '0');          // Column 7: Bank Transfer
      const cashCollected = parseFloat(cells[8] || '0');         // Column 8: Cash Collected
      
      // CORRECT CALCULATION: What driver actually owes company
      // = Total Income - Cash Already With Driver - Toll Refunds
      const actualDriverCredit = totalIncome - cashCollected - refundsAndExpenses;
      
      // Detect company total row (Sharjah Transport Solutions)
      const isCompanyRow = driverName.includes('Sharjah Transport Solutions') || 
                          driverName.includes('شارجة') || 
                          firstName.includes('Sharjah');

      // Debug logging for first few records and company row
      if (i < 5 || isCompanyRow) {
        console.log(`🔍 Processing record ${i + 1}:`);
        console.log(`  UUID: "${driverUuid}"`);
        console.log(`  Name: "${driverName}"`);
        console.log(`  Is Company Row: ${isCompanyRow}`);
        console.log(`  Total Income: ${totalIncome}`);
        console.log(`  Cash Collected: ${cashCollected}`);
        console.log(`  Refunds/Expenses: ${refundsAndExpenses}`);
        console.log(`  Income Amounts: ${incomeAmounts}`);
        console.log(`  Actual Driver Credit: ${actualDriverCredit}`);
      }

      // Handle company total row separately
      if (isCompanyRow) {
        companyTotal = Math.abs(incomeAmounts); // This is the bank deposit amount
        console.log(`🏢 COMPANY ROW DETECTED: Bank deposit = ${companyTotal} AED`);
        
        // Store company row in raw table with flag
        rawRecords.push({
          uber_driver_uuid: driverUuid,
          first_name: firstName,
          last_name: lastName,
          full_name: driverName,
          total_income: totalIncome,
          net_fare: netFare,
          refunds_and_expenses: refundsAndExpenses,
          refunds_and_expenses_amount: refundsAndExpenses,
          income_amounts: incomeAmounts,
          bank_transfer: bankTransfer,
          cash_collected: cashCollected,
          actual_driver_credit: actualDriverCredit,
          company_total_flag: true,
          processed_to_accounting: false,
          driver_id: null,
          report_week_start: dateRange.startDate,
          report_week_end: dateRange.endDate,
          import_batch_id: batchId,
          file_hash: fileHash
        });
        continue; // Skip to next record
      }

      // Process regular driver rows
      if (actualDriverCredit > 0 && driverName !== '') {
        const result = await findUberDriverOptimized(driverUuid, driverName);
        const transactionDate = calculateTransactionDate(dateRange.startDate, dateRange.endDate, startIndex + i, chunk.length);
        
        if (i < 5) {
          console.log(`  ✅ Processing driver record:`);
          console.log(`  Driver match: ${result.matchType} -> ${result.driver ? result.driver.name : 'NOT FOUND'}`);
          console.log(`  Transaction date: ${transactionDate}`);
          console.log(`  Amount to record: ${actualDriverCredit} AED`);
        }
        
        // Add to raw records with all columns
        rawRecords.push({
          uber_driver_uuid: driverUuid,
          first_name: firstName,
          last_name: lastName,
          full_name: driverName,
          total_income: totalIncome,
          net_fare: netFare,
          refunds_and_expenses: refundsAndExpenses,
          refunds_and_expenses_amount: refundsAndExpenses,
          income_amounts: incomeAmounts,
          bank_transfer: bankTransfer,
          cash_collected: cashCollected,
          actual_driver_credit: actualDriverCredit,
          company_total_flag: false,
          processed_to_accounting: result.driver ? true : false,
          driver_id: result.driver?.id || null,
          report_week_start: dateRange.startDate,
          report_week_end: dateRange.endDate,
          import_batch_id: batchId,
          file_hash: fileHash
        });
        
        if (result.driver) {
          if (result.matchType === 'uuid') uuidMatches++;
          else if (result.matchType === 'name') nameMatches++;
          
          // Use CORRECTED amount in transaction
          transactions.push({
            transaction_date: transactionDate,
            type: 'earning',
            category: 'uber_earnings',
            amount: actualDriverCredit, // ← FIXED: Using correct calculation
            description: `Uber earnings - ${driverName} (${dateRange.startDate} to ${dateRange.endDate})`,
            driver_id: result.driver.id,
            import_batch_id: batchId,
            batch_start_date: dateRange.startDate,
            batch_end_date: dateRange.endDate,
            source_filename: selectedFile?.name || 'unknown_file',
            records_in_batch: chunk.length,
            is_company_level: false
          });

          totalAmount += actualDriverCredit;
          driverSum += actualDriverCredit;
          processed++;
        } else {
          notFound++;
        }
      } else {
        skippedRecords++;
        if (i < 5) {
          console.log(`  ❌ Skipping record: actualCredit=${actualDriverCredit}, name="${driverName}"`);
        }
      }
    }

    console.log(`✅ Chunk processing complete:`);
    console.log(`  - Processed: ${processed}`);
    console.log(`  - UUID matches: ${uuidMatches}`);
    console.log(`  - Name matches: ${nameMatches}`);
    console.log(`  - Not found: ${notFound}`);
    console.log(`  - Skipped: ${skippedRecords}`);
    console.log(`  - Driver Sum: ${driverSum} AED`);
    console.log(`  - Company Total: ${companyTotal} AED`);
    console.log(`  - Raw records to insert: ${rawRecords.length}`);
    console.log(`  - Transactions to insert: ${transactions.length}`);

    // Batch insert raw records
    if (rawRecords.length > 0) {
      console.log(`💾 Inserting ${rawRecords.length} raw records...`);
      const { data: rawData, error: rawError } = await supabase
        .from('uber_income_raw')
        .insert(rawRecords);
      
      if (rawError) {
        console.error('❌ Failed to insert raw records:', rawError);
      } else {
        console.log('✅ Raw records inserted successfully');
      }
    } else {
      console.log('⚠️ No raw records to insert');
    }

    // Batch insert transactions
    if (transactions.length > 0) {
      console.log(`💾 Inserting ${transactions.length} transactions...`);
      const { data: transData, error: transError } = await supabase
        .from('accounting_transactions')
        .insert(transactions);
      
      if (transError) {
        console.error('❌ Failed to insert transactions:', transError);
      } else {
        console.log('✅ Transactions inserted successfully');
      }
    } else {
      console.log('⚠️ No transactions to insert');
    }

    return {
      processed,
      totalAmount,
      uuidMatches,
      nameMatches,
      notFound,
      companyTotal,
      driverSum
    };
  };

  // Optimized driver lookup with caching
  const driverCache = useRef(new Map());
  
  const findUberDriverOptimized = async (driverUuid: string, driverName: string) => {
    const cacheKey = `${driverUuid}_${driverName}`;
    
    if (driverCache.current.has(cacheKey)) {
      return driverCache.current.get(cacheKey);
    }

    try {
      let result = { driver: null, matchType: 'none' };

      // Try UUID match first
      if (driverUuid && driverUuid.trim() !== '') {
        const { data: driverByUuid } = await supabase
          .from('drivers')
          .select('id, name, uber_uuid')
          .eq('uber_uuid', driverUuid.trim())
          .limit(1);
        
        if (driverByUuid && driverByUuid.length > 0) {
          result = { driver: driverByUuid[0], matchType: 'uuid' };
        }
      }
      
      // Fallback to name matching
      if (!result.driver && driverName && driverName.trim() !== '') {
        const { data: driversByName } = await supabase
          .from('drivers')
          .select('id, name, uber_uuid')
          .ilike('name', `%${driverName.trim()}%`)
          .limit(1);
        
        if (driversByName && driversByName.length > 0) {
          result = { driver: driversByName[0], matchType: 'name' };
        }
      }

      // Cache the result
      driverCache.current.set(cacheKey, result);
      return result;
    } catch (error) {
      console.warn(`Error finding driver ${driverName}:`, error);
      const result = { driver: null, matchType: 'error' };
      driverCache.current.set(cacheKey, result);
      return result;
    }
  };

  // Helper functions
  const calculateFileHash = async (content: string): Promise<string> => {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      return 'hash_failed_' + Date.now();
    }
  };

  const extractDateRangeFromFilename = (filename: string) => {
    console.log(`🔍 Extracting dates from filename: "${filename}"`);
    
    // Try YYYYMMDD-YYYYMMDD format first (like 20250701-20250710)
    const compactDatePattern = /(\d{8})-(\d{8})/;
    const compactMatch = filename.match(compactDatePattern);
    
    if (compactMatch) {
      const startDateStr = compactMatch[1]; // 20250701
      const endDateStr = compactMatch[2];   // 20250710
      
      // Convert YYYYMMDD to YYYY-MM-DD
      const startDate = `${startDateStr.slice(0,4)}-${startDateStr.slice(4,6)}-${startDateStr.slice(6,8)}`;
      const endDate = `${endDateStr.slice(0,4)}-${endDateStr.slice(4,6)}-${endDateStr.slice(6,8)}`;
      
      console.log(`✅ Found compact date range: ${startDate} to ${endDate}`);
      return { startDate, endDate };
    }
    
    // Try standard YYYY-MM-DD format
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      console.log(`✅ Found standard date range: ${dates[0]} to ${dates[dates.length - 1]}`);
      return { startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    
    // Try alternative date formats (MM/DD/YYYY, DD-MM-YYYY, etc.)
    const altDatePattern = /(\d{1,2}[-\/]\d{1,2}[-\/]\d{4})/g;
    const altDates = filename.match(altDatePattern);
    
    if (altDates && altDates.length >= 2) {
      // Convert to YYYY-MM-DD format
      const convertedDates = altDates.map(dateStr => {
        const parts = dateStr.split(/[-\/]/);
        if (parts.length === 3) {
          // Assume MM/DD/YYYY or DD/MM/YYYY format
          const year = parts[2];
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return dateStr;
      });
      console.log(`✅ Found alternative date range: ${convertedDates[0]} to ${convertedDates[convertedDates.length - 1]}`);
      return { startDate: convertedDates[0], endDate: convertedDates[convertedDates.length - 1] };
    }
    
    // If no dates found in filename, use a broader range for July 2025
    console.log(`⚠️ No dates found in filename, using default July 2025 range`);
    return {
      startDate: '2025-07-01',
      endDate: '2025-07-31'
    };
  };

  const calculateTransactionDate = (startDate: string, endDate: string, recordIndex: number, totalRecords: number) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    const dayOffset = Math.floor((recordIndex / totalRecords) * totalDays);
    const transactionDate = new Date(start);
    transactionDate.setDate(start.getDate() + dayOffset);
    
    return transactionDate.toISOString().split('T')[0];
  };

  const resetImport = () => {
    setSelectedFile(null);
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 6,
      message: 'Ready to import Uber data',
    });
    setImportStats(null);
    setIsProcessing(false);
    driverCache.current.clear();
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber && isProcessing) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#000000'; // Uber black
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#000000" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Uber Import (Fixed)" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#000000' + '15' }]}>
              <Ionicons name="car" size={24} color="#000000" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Uber Import (Optimized)</Text>
              <Text style={styles.subtitle}>Fixed background processing & chunked imports</Text>
            </View>
          </View>

          {/* Progress Indicator */}
          {isProcessing && (
            <View style={styles.backgroundWarning}>
              <Ionicons name="shield-checkmark" size={20} color="#000000" />
              <Text style={styles.backgroundText}>
                Safe to minimize app - import will continue in background
              </Text>
            </View>
          )}

          {/* File Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Select File</Text>
            <TouchableOpacity 
              style={[styles.filePickerButton, { borderColor: '#000000' }]} 
              onPress={pickFile}
              disabled={isProcessing}
            >
              <Ionicons name="cloud-upload" size={20} color="#000000" />
              <Text style={[styles.filePickerText, { color: '#000000' }]}>
                {selectedFile ? selectedFile.name : 'Choose Uber CSV file'}
              </Text>
            </TouchableOpacity>
            {selectedFile && (
              <View style={styles.fileInfo}>
                <Ionicons name="document-text" size={16} color={theme.colors.success} />
                <Text style={styles.fileInfoText}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </View>
            )}
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Import Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#000000' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {isProcessing && (
                    <ActivityIndicator size="small" color="#000000" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressContainer}>
                    <View style={styles.progressBar}>
                      <View 
                        style={[
                          styles.progressFill, 
                          { 
                            width: `${(progress.processed / progress.total) * 100}%`,
                            backgroundColor: '#000000'
                          }
                        ]} 
                      />
                    </View>
                    <Text style={styles.progressText}>
                      {progress.processed}/{progress.total} ({Math.round((progress.processed / progress.total) * 100)}%)
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Import Results */}
          {importStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>3. Import Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{importStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#000000' }]}>
                    {importStats.uuidMatches}
                  </Text>
                  <Text style={styles.statLabel}>UUID Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.warning }]}>
                    {importStats.nameMatches}
                  </Text>
                  <Text style={styles.statLabel}>Name Matches</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.error }]}>
                    {importStats.notFound}
                  </Text>
                  <Text style={styles.statLabel}>Not Found</Text>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Amount:</Text>
                  <Text style={styles.summaryValue}>
                    AED {importStats.totalAmount.toLocaleString()}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Accuracy:</Text>
                  <Text style={[styles.summaryValue, { color: '#000000' }]}>
                    {importStats.accuracy}%
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Batch ID:</Text>
                  <Text style={styles.summaryValue}>
                    {importStats.batchId}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {!isProcessing ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#000000' },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(false)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="cloud-upload" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    Import with Duplicate Check
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#FF8800', marginTop: 8 },
                    !selectedFile && styles.disabledButton
                  ]} 
                  onPress={() => performOptimizedImport(true)}
                  disabled={!selectedFile}
                >
                  <Ionicons name="flash" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Quick Import (Skip Duplicates)</Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#000000', marginTop: 8 }]} 
                    onPress={resetImport}
                  >
                    <Ionicons name="refresh" size={20} color="#000000" />
                    <Text style={[styles.secondaryButtonText, { color: '#000000' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <View style={styles.processingContainer}>
                <TouchableOpacity style={styles.disabledButton} disabled>
                  <ActivityIndicator size="small" color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>Processing... (Safe to minimize)</Text>
                </TouchableOpacity>
                <Text style={styles.processingNote}>
                  Import will continue in background. You can safely switch to other apps.
                </Text>
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  backgroundWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#000000' + '10',
    borderWidth: 1,
    borderColor: '#000000' + '30',
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
  },
  backgroundText: {
    fontSize: theme.typography.sizes.sm,
    color: '#000000',
    fontWeight: '500' as any,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  filePickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#000000' + '05',
    gap: theme.spacing.sm,
  },
  filePickerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
  },
  fileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.success + '10',
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.xs,
  },
  fileInfoText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.success,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressContainer: {
    gap: theme.spacing.xs,
  },
  progressBar: {
    height: 6,
    backgroundColor: theme.colors.gray200,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  progressText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  processingContainer: {
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  processingNote: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export default UberImportScreenFixed;
